import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
//import { Badge, Table } from "react-bootstrap"; // Using standard bootstrap table for now
import { formInputStyles, formStyles } from "../../../shared/constants/styles";
import {
  colorGrayNew,
  colorGreen,
  colorGreenHover,
  colorRed,
} from "../../../utils/palette";
import {
  Desc,
  FormGroupLabel,
  FormItem,
} from "../../../shared/components/form/FormElements";
import { Table } from "../../../shared/components/TableElements";

import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import AssetDropdown from "../../../shared/components/AssetDropdown";
import { getListingDisplayDate } from "../../../shared/helpers";
import getUnixTime from "date-fns/getUnixTime";
import {
  FilterButton,
  LinkedVehicleElement,
} from "../../../shared/components/TableElements";
import VehicleDetailsPopup from "../../../shared/components/VehicleDetailsPopup";
import { NATEXT, s3Url } from "../../../shared/constants";
import defaultVehicleIcon from "@/shared/img/defaultVehicleIcon.png";
import { getVehicleBorderColor } from "../../../utils/helpers";
import { post } from "../../../api/getUser";
import { TableWrap } from "../../../shared/components/table/components/ReactTableConstructor";

const LinkedVehicleTab = ({ rowData, type }) => {
  const { t } = useTranslation("common");

  const {
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const users = useSelector((state) => state.users.list);

  const { userName, orgId } = useSelector((state) => state.user);

  const linkedVehicle = watch("linkedVehicle");
  const status = watch("status");

  // State for Linked Vehicle
  const [rawLinkHistory, setRawLinkHistory] = useState([]);

  const [showLinkedVehicleDetails, setShowLinkedVehicleDetails] =
    useState(false);

  const vehicles = useSelector((state) => state.vehicles.list);

  // Options for the dropdown
  const vehicleOptions = useMemo(
    () => vehicles.filter((v) => v.veh_active === 1 && v.asset_type === 0),
    [vehicles]
  );

  const vehiclesIncludingInactive = useMemo(
    () => vehicles.filter((v) => v.asset_type === 0),
    [vehicles]
  );

  //Edit effect
  useEffect(() => {
    if (type === "edit" && rowData && vehicleOptions?.length > 0) {
      const { linked_veh } = rowData;
      if (linked_veh) {
        const linkedVehicle = vehicleOptions.find(
          (v) => v.veh_id === linked_veh
        );
        if (linkedVehicle) {
          setValue("linkedVehicle", linkedVehicle);
        } else {
          setValue("linkedVehicle", {
            veh_id: linked_veh,
            name: t("unassigned_asset"),
            value: null,
          });
        }
      }
    }
  }, [rowData, type, vehicleOptions]);

  const vehId = rowData?.veh_id;

  //Set link history data on edit
  //Including inactive since link history could contain inactive vehicles
  //So writng a separate effect for this
  useEffect(() => {
    if (type === "edit" && vehId) {
      //Get link history data
      (async () => {
        const linkHistoryRes = await post("vehicle", {
          operation: "fetch_link_history",
          se_veh_id: vehId,
        });
        if (linkHistoryRes?.success) {
          const linkHistoryData = linkHistoryRes?.data;
          //console.log("linkHistoryData", linkHistoryData);

          if (linkHistoryData?.length > 0) {
            setRawLinkHistory(linkHistoryData);
          }
        }
      })();
    }
  }, [type, vehId]);

  const { linkHistory, linkedOnText, activeLinkedVehId } = useMemo(() => {
    let processedHistory = [];
    let text = "";
    let activeId = null;

    if (rawLinkHistory?.length > 0) {
      for (let linkData of rawLinkHistory) {
        const { veh_id, start_date, end_date, link_by_name } = linkData;

        //set linked on text
        if (end_date === 0) {
          text = t("linked_on_text", {
            date: getListingDisplayDate(start_date),
            user: link_by_name,
          });
          activeId = veh_id;
        }

        let singleLink = {
          vehicle_name: t("unassigned_asset"),
          license_no: "",
          imgUrl: defaultVehicleIcon,
          start_date,
          end_date,
          link_by_name,
        };

        if (vehiclesIncludingInactive?.length > 0) {
          const vehicleExists = vehiclesIncludingInactive.find(
            (v) => v.veh_id === veh_id
          );
          if (vehicleExists) {
            const { name, license_no, veh_img } = vehicleExists;
            singleLink.vehicle_name = name;
            singleLink.license_no = license_no;
            singleLink.imgUrl = veh_img
              ? `${s3Url}/org_${orgId}/vehicle_${veh_id}/${veh_img}`
              : defaultVehicleIcon;
          }
        }
        processedHistory.push(singleLink);
      }
    }
    return {
      linkHistory: processedHistory,
      linkedOnText: text,
      activeLinkedVehId: activeId,
    };
  }, [rawLinkHistory, vehiclesIncludingInactive, t, orgId]);

  const handleUnlink = () => {
    setValue("linkedVehicle", null);
  };

  const imageUrl = linkedVehicle?.veh_img
    ? `${s3Url}/org_${orgId}/vehicle_${linkedVehicle.veh_id}/${linkedVehicle.veh_img}`
    : defaultVehicleIcon;

  const vehicleBorderColor = getVehicleBorderColor(
    linkedVehicle?.veh_active,
    linkedVehicle?.secondary_status
  );

  return (
    <Container>
      {/* Current Link Status Section */}
      <SectionTitle>
        {linkedVehicle ? t("currently_linked_vehicle") : t("link_a_vehicle")}
      </SectionTitle>

      {linkedVehicle ? (
        <LinkedVehicleBox>
          <div className="d-flex gap-2 align-items-center">
            {/* <div
              style={{
                border: `1px solid ${vehicleBorderColor}`,
                borderRadius: "20px",
              }}
            > */}
            <img
              src={imageUrl}
              alt="vehicle-img"
              style={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                border: `1px solid ${vehicleBorderColor}`,
                background: "#D4E5F6",
                //filter: isInactiveVehicle ? "grayscale(100%)" : "none",
                padding: "2px",
              }}
              className="rounded-circle"
            />
            {/* </div> */}
            <div className="d-flex flex-column gap-1">
              {linkedVehicle?.value ? (
                <LinkedVehicleElement
                  onClick={() => setShowLinkedVehicleDetails(true)}
                >
                  {`${linkedVehicle.name} ${
                    linkedVehicle.license_no
                      ? `- ${linkedVehicle.license_no}`
                      : ""
                  }`}
                </LinkedVehicleElement>
              ) : (
                <p>{t("unassigned_asset")}</p>
              )}
              <span>
                {linkedVehicle?.veh_id === activeLinkedVehId
                  ? linkedOnText
                  : ""}
              </span>
            </div>
          </div>
          {/* {!readOnly && ( */}
          <FilterButton
            type="button"
            onClick={handleUnlink}
            style={{ minWidth: "min-content" }}
          >
            {t("unlink") || "Unlink"}
          </FilterButton>
          {/* )} */}
        </LinkedVehicleBox>
      ) : (
        <LinkSection>
          <FormGroupLabel
            className="mb-1 mt-1"
            //style={{ fontWeight: 400, marginTop: "10px" }}
          >
            {t("select_vehicle") || "Select Vehicle"}
          </FormGroupLabel>
          <div style={{ maxWidth: "350px" }}>
            <AssetDropdown
              assets={vehicleOptions}
              vehicle={linkedVehicle}
              onChange={(val) => {
                setValue("linkedVehicle", val);
              }}
              isEquipmentSupported={false}
              placeholder={t("search_vehicles")}
              isDisabled={!status}
              isForm
              hideFilters
            />
          </div>
          <Desc className="mt-2">
            {!status
              ? t("active_equip_to_link_msg")
              : t("link_vehicle_helper_text")}
          </Desc>
        </LinkSection>
      )}

      {/* Link History Section */}
      <SectionTitle style={{ marginTop: "30px" }}>
        {t("link_history") || "Link History"}
      </SectionTitle>
      <TableWrap height={window.innerHeight - 400}>
        <Table
          bordered
          id="link-history-table"
          //className="table-hover"
        >
          <thead>
            <tr>
              <th>{t("veh_name")}</th>
              <th>{t("license_plate")}</th>
              <th>{t("start_date")}</th>
              <th>{t("end_date")}</th>
              <th>{t("linked_by")}</th>
            </tr>
          </thead>
          <tbody>
            {linkHistory && linkHistory.length > 0 ? (
              linkHistory.map((item, index) => (
                <tr
                  key={index}
                  //style={{ color: "black", fontWeight: 400, fontSize: 14 }}
                >
                  <TD>
                    <div className="d-flex gap-2 align-items-center">
                      <img
                        src={item.imgUrl}
                        alt="vehicle-img"
                        style={{
                          width: "20px",
                          height: "20px",
                          objectFit: "cover",
                          border: `1px solid #009BD9`,
                          background: "#D4E5F6",
                          //filter: isInactiveVehicle ? "grayscale(100%)" : "none",
                          padding: "2px",
                        }}
                        className="rounded-circle"
                      />
                      <p>{item.vehicle_name}</p>
                    </div>
                  </TD>
                  <TD>{item.license_no || NATEXT}</TD>
                  <TD>{getListingDisplayDate(item.start_date)}</TD>
                  <TD>
                    {item.end_date
                      ? getListingDisplayDate(item.end_date)
                      : NATEXT}
                  </TD>
                  <TD>{item.link_by_name}</TD>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  {t("no_history_found")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrap>
      {showLinkedVehicleDetails && (
        <VehicleDetailsPopup
          showPopup={showLinkedVehicleDetails}
          setShowPopup={setShowLinkedVehicleDetails}
          vehicleId={linkedVehicle?.veh_id}
        />
      )}
    </Container>
  );
};

export default LinkedVehicleTab;

// Styled Components

const Container = styled.div`
  padding: 1rem;
`;

const SectionTitle = styled.h5`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const LinkSection = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 0px;
  /* match standard form padding if needed, but container has padding */
`;

const HelperText = styled.p`
  font-size: 13px;
  color: #666;
  margin-top: 8px;
`;

const LinkedVehicleBox = styled.div`
  border: 1px solid ${colorGrayNew};
  border-radius: 5px;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

//Create TD component
const TD = styled.td`
  //vertical-align: middle;
  font-size: 14px;
  color: black !important;
  font-weight: 400;
`;

// const HistoryTable = styled(Table)`
//   & thead th {
//     border-top: none;
//     border-bottom: 1px solid #dee2e6;
//     font-size: 13px;
//     font-weight: 600;
//     color: #333;
//   }

//   & tbody td {
//     font-size: 14px;
//     vertical-align: middle;
//     color: #495057;
//   }
// `;
